import { AriaAttributes, FC } from 'react';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { StringKey } from '../../../../lang/index';
import { Order } from '../../../../types/general.types';

/**
 * A table header, a button where the server can re-sort by it. Both read exactly alike — the design
 * draws no arrow — and the table's `aria-sort` says which column is in charge. A column that does
 * not sort is plain text, rather than a button announced as unavailable.
 */
export const SortableTitle: FC<{ onSort?: () => void; title: StringKey }> = ({ onSort, title }) =>
  onSort ? (
    <Button
      className="w-fit cursor-pointer justify-start px-0 text-label-md font-medium text-[#172335]"
      onClick={onSort}
      styleType="NONE"
    >
      <AppFormattedMessage id={title} />
    </Button>
  ) : (
    <span className="text-label-md font-medium text-[#172335]">
      <AppFormattedMessage id={title} />
    </span>
  );

/** What each sortable column announces: the server's current sort on its own column, else none. */
export const ariaSortOf = <TSort,>(
  sortable: Partial<Record<string, TSort>>,
  sortBy: TSort,
  sortOrder: Order,
): Record<string, AriaAttributes['aria-sort']> =>
  Object.fromEntries(
    Object.entries(sortable).map(([column, sort]) => [
      column,
      sort === sortBy ? (sortOrder === Order.ASC ? 'ascending' : 'descending') : 'none',
    ]),
  );
